"use client";
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import lottie from "lottie-web/build/player/lottie_light";

/**
 * Stripped-down lottie player for the masthead background.
 *
 * Faster than the generic LottieComponent because it removes the intermediate
 * useState/re-render cycle:
 *   Before: fetch → setState(data) → re-render → useLottie init → first frame
 *   Now:    fetch → lottie.loadAnimation() directly → first frame
 *
 * fetch() hits the preload cache set by MastHeadPreload, so the JSON arrives
 * from memory — the only remaining delay is JSON.parse + lottie init.
 */
const MastHeadLottie = ({ src }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !src) return;

    let anim;
    let destroyed = false;

    // fetch() hits the preload cache — near-zero network cost
    fetch(src)
      .then((r) => r.json())
      .then((data) => {
        if (destroyed || !containerRef.current) return;
        anim = lottie.loadAnimation({
          container: containerRef.current,
          renderer: "svg",
          loop: true,
          autoplay: true,
          animationData: data,
        });
      })
      .catch(() => {});

    return () => {
      destroyed = true;
      anim?.destroy();
    };
  }, [src]);

  return <div ref={containerRef} className="h-full w-full" />;
};

MastHeadLottie.propTypes = {
  src: PropTypes.string.isRequired,
};

export default MastHeadLottie;
